import React, { useEffect, useRef, useState } from 'react'

import { useApi } from 'hooks/useApi'
import { ConfigUtils } from 'utils/config'

import StoresAndDateFilter from '../StoresAndDateFilter/StoresAndDateFilter'
import CohortAnalysisTable from './CohortAnalysisTable/CohortAnalysisTable'
import BenefitsUsed from './BenefitsUsed/BenefitsUsed'
import UserTest from './UserTest/UserTest'
import WelcomeRewardUsers from './WelcomeRewardUses/WelcomeRewardUses'
import FunnelGeneral from './FunnelGeneral/FunnelGeneral'
import RateMetrics from './RateMetrics/RateMetrics'
import { getStores } from '../../Stores/api'
import { GetStoresResponse, Store } from '../../Stores/types'
import { useDashboardFilters } from '../contexts/DashboardFiltersContext'

const isGEConnection = ConfigUtils.isGEConnection()

const LoyaltyEfficiencyTab = () => {
  const {
    filters,
    handlePeriodChange,
    handleStoreChange,
    handleCompareStoreChange,
    handleComparaChange,
    handleApply,
    isActiveTab,
  } = useDashboardFilters()
  const { period, store, compareStore, compara, applyTrigger, isLoading } = filters

  // Get stores for mapping
  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(
    getStores,
    response => response?.stores || [],
    { baseData: [] },
  )

  // Convert store names to store IDs for API calls
  const getStoreIds = () => {
    const selectedStores: number[] = []

    // Add main store
    if (store !== 'all') {
      const mainStore = stores.find(s => s.name === store)
      if (mainStore) {
        selectedStores.push(mainStore.id)
      }
    }

    // Add comparison store if comparison is enabled
    if (compara && compareStore !== 'all') {
      const compareStoreObj = stores.find(s => s.name === compareStore)
      if (compareStoreObj && !selectedStores.includes(compareStoreObj.id)) {
        selectedStores.push(compareStoreObj.id)
      }
    }

    return selectedStores
  }

  // Only trigger API calls if this tab is active
  const shouldTrigger = isActiveTab('loyalty-efficiency')

  // Ejecutar indicadores secuencialmente: uno termina -> arranca el siguiente
  const [step, setStep] = useState(0)
  const runRef = useRef(0)

  useEffect(() => {
    if (!shouldTrigger) return
    runRef.current = applyTrigger
    setStep(0)
  }, [applyTrigger, shouldTrigger])

  return (
    <div className="flex flex-col gap-8 p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-row items-end gap-4 w-full">
        <StoresAndDateFilter
          storesList={stores}
          period={period}
          onPeriodChange={handlePeriodChange}
          store={store}
          onStoreChange={handleStoreChange}
          compara={compara}
          onComparaChange={handleComparaChange}
          compareStore={compareStore}
          onCompareStoreChange={handleCompareStoreChange}
          onApply={handleApply}
          isLoading={isLoading}
        />
      </div>
      <CohortAnalysisTable
        period={period}
        storeId={getStoreIds()[0]}
        trigger={shouldTrigger && step === 0 ? applyTrigger : 0}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 0 ? 1 : prev))
        }}
      />
      <BenefitsUsed
        period={period}
        storeIds={getStoreIds()}
        trigger={shouldTrigger && step === 1 ? applyTrigger : 0}
        compara={compara}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 1 ? 2 : prev))
        }}
      />
      <UserTest
        period={period}
        trigger={shouldTrigger && step === 2 ? applyTrigger : 0}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 2 ? 3 : prev))
        }}
      />
      <WelcomeRewardUsers
        period={period}
        storeIds={getStoreIds()}
        stores={stores}
        trigger={shouldTrigger && step === 3 ? applyTrigger : 0}
        compara={false}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 3 ? 4 : prev))
        }}
      />
      {/* Solo mostrar Funnel General y Rate Metrics en GE */}
      {isGEConnection && (
        <>
          <FunnelGeneral
            period={period}
            trigger={shouldTrigger && step === 4 ? applyTrigger : 0}
            onLoaded={() => {
              if (runRef.current !== applyTrigger) return
              setStep(prev => (prev === 4 ? 5 : prev))
            }}
          />
          <RateMetrics
            period={period}
            storeIds={getStoreIds()}
            stores={stores}
            trigger={shouldTrigger && step === 5 ? applyTrigger : 0}
            onLoaded={() => {
              if (runRef.current !== applyTrigger) return
              setStep(prev => (prev === 5 ? 6 : prev))
            }}
          />
        </>
      )}
    </div>
  )
}

export default LoyaltyEfficiencyTab
